import React, { useState } from "react";
import { Pencil } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { StatusChip, StatusEditor, stamp } from "@/components/attendance/AttendanceEditors";
import { summarize, AttendanceCounts } from "@/components/MemberAttendanceCard";
import { formatDateOnly } from "@/lib/date";

function MemberLine({ row, event, canEdit, editing, setEditing, onSave }) {
  const status = row.cells?.[event.id];
  const note = row.notes?.[event.id];
  const isEditing = editing === row.member_id;
  return (
    <div className={`rounded-lg border p-2.5 transition-colors ${isEditing ? "border-slate-300 bg-slate-50" : "border-slate-200"}`} data-testid={`session-member-${row.member_id}`}>
      <button type="button" disabled={!canEdit} onClick={() => setEditing(isEditing ? null : row.member_id)} className="flex w-full items-center justify-between gap-2 text-left disabled:cursor-default" data-testid={`session-member-toggle-${row.member_id}`}>
        <span className="min-w-0">
          <span className="block truncate text-sm font-medium text-slate-800">{row.member_name}</span>
          {note && <span className="block truncate text-xs italic text-slate-500">“{note}”</span>}
        </span>
        <span className="flex shrink-0 items-center gap-2"><StatusChip status={status} testId={`session-status-${row.member_id}`} />{canEdit && <Pencil className="h-3.5 w-3.5 text-slate-300" />}</span>
      </button>
      {isEditing && (
        <div className="mt-2 border-t border-slate-200 pt-2">
          <StatusEditor status={status} note={note} testPrefix={`session-edit-${row.member_id}`}
            onSave={(st, nt) => onSave(event.id, row.member_id, st, nt)} onDone={() => setEditing(null)} />
        </div>
      )}
    </div>
  );
}

// One past session: every member's status; tap a member to correct it.
export default function SessionDialog({ event, rows, canEdit, onSave, onClose }) {
  const [editing, setEditing] = useState(null);
  if (!event) return null;
  const s = summarize(rows.map((r) => ({ status: r.cells?.[event.id] })).filter((r) => r.status));
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] max-w-lg overflow-y-auto" data-testid="attendance-session-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">{event.title || "Session"}</DialogTitle>
          <DialogDescription>{formatDateOnly(event.event_date)}{canEdit ? " · Tap a team member to change their status." : ""}</DialogDescription>
        </DialogHeader>
        <div className="space-y-0.5 rounded-lg bg-slate-50 px-3 py-2 text-xs text-slate-500">
          {event.taken_by_name && <p data-testid="session-taken-by">Taken by {event.taken_by_name} · {stamp(event.submitted_at)}</p>}
          {event.edited_by_name && <p className="font-medium text-amber-700" data-testid="session-edited-by">Edited by {event.edited_by_name} · {stamp(event.edited_at)}</p>}
        </div>
        <div><AttendanceCounts s={s} testPrefix="session" /></div>
        <div className="space-y-2" data-testid="session-members">
          {rows.map((r) => <MemberLine key={r.member_id} row={r} event={event} canEdit={canEdit} editing={editing} setEditing={setEditing} onSave={onSave} />)}
        </div>
      </DialogContent>
    </Dialog>
  );
}
